import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import {
  AdminsNavIcon,
  AppConfigsNavIcon,
  AreasNavIcon,
  FeatureFlagsNavIcon,
  HomeNavIcon,
  IntegrationLogsNavIcon,
  SellersNavIcon,
  StoreSubscriptionsNavIcon,
  StoresNavIcon,
  SubscriptionPlansNavIcon,
  ThemesNavIcon,
} from "./nav-icons";

// ─────────────────────────────────────────────────────────────────────────────
// Admin navigation model — one source of truth consumed by the desktop
// Sidebar, the mobile BottomNav, and the top-bar label. Mirrors the seller
// portal's grouped-nav shape (Sidebar groups / BottomNav tabs).
// ─────────────────────────────────────────────────────────────────────────────

export type NavItem = {
  /** Stable id (also used as the React key). */
  key: string;
  href: string;
  label: string;
  icon: ReactNode;
  isActive: boolean;
};

export type NavGroup = {
  /** Omitted for the primary (un-headed) tier. */
  header?: string;
  items: NavItem[];
};

export function useAdminNav({
  locale,
  isSuperAdmin,
}: {
  locale: string;
  isSuperAdmin?: boolean;
}): {
  groups: NavGroup[];
  /** All destinations flattened in group order (mobile menu list). */
  tabs: NavItem[];
  /** The currently-active destination, if any. */
  activeItem: NavItem | undefined;
} {
  const { pathname } = useLocation();
  const t = useTranslations();

  const base = `/${locale}/admin`;
  const sellersHref = `${base}/sellers`;
  const themesHref = `${base}/themes`;
  const storesHref = `${base}/stores`;
  const areasHref = `${base}/areas`;
  const areaSuggestionsHref = `${base}/area-suggestions`;
  const storeSubscriptionsHref = `${base}/stores/subscriptions`;
  const subscriptionsHref = `${base}/subscriptions`;
  const integrationLogsHref = `${base}/integration-logs`;
  const adminsHref = `${base}/admins`;
  const appConfigsHref = `${base}/app-configs`;
  const featureFlagsHref = `${base}/feature-flags`;

  const isStoreSubscriptionsActive = pathname?.startsWith(
    storeSubscriptionsHref,
  );

  const groups: NavGroup[] = [
    {
      items: [
        {
          key: "dashboard",
          href: base,
          label: t(keys.admin.dashboardTitle),
          icon: <HomeNavIcon />,
          isActive: pathname === base || pathname === `${base}/`,
        },
      ],
    },
    {
      header: t(keys.admin.navGroupPlatform),
      items: [
        {
          key: "sellers",
          href: sellersHref,
          label: t(keys.admin.sellersTitle),
          icon: <SellersNavIcon />,
          isActive: Boolean(pathname?.startsWith(sellersHref)),
        },
        {
          key: "stores",
          href: storesHref,
          label: t(keys.admin.storesTitle),
          icon: <StoresNavIcon />,
          isActive: Boolean(
            pathname?.startsWith(storesHref) && !isStoreSubscriptionsActive,
          ),
        },
        {
          key: "areas",
          href: areasHref,
          label: t(keys.areas.nav),
          icon: <AreasNavIcon />,
          isActive: Boolean(pathname?.startsWith(areasHref)),
        },
        {
          key: "areaSuggestions",
          href: areaSuggestionsHref,
          label: t(keys.areaSuggestions.nav),
          icon: <AreasNavIcon />,
          isActive: Boolean(pathname?.startsWith(areaSuggestionsHref)),
        },
        {
          key: "themes",
          href: themesHref,
          label: t(keys.themes.nav),
          icon: <ThemesNavIcon />,
          isActive: Boolean(pathname?.startsWith(themesHref)),
        },
      ],
    },
    {
      header: t(keys.admin.navGroupBilling),
      items: [
        {
          key: "storeSubscriptions",
          href: storeSubscriptionsHref,
          label: t(keys.admin.storeSubscriptionsTitle),
          icon: <StoreSubscriptionsNavIcon />,
          isActive: Boolean(isStoreSubscriptionsActive),
        },
        {
          key: "subscriptions",
          href: subscriptionsHref,
          label: t(keys.admin.subscriptionsTitle),
          icon: <SubscriptionPlansNavIcon />,
          isActive: Boolean(pathname?.startsWith(subscriptionsHref)),
        },
      ],
    },
    {
      header: t(keys.admin.navGroupSystem),
      items: [
        {
          key: "integrationLogs",
          href: integrationLogsHref,
          label: t(keys.integrationLogs.nav),
          icon: <IntegrationLogsNavIcon />,
          isActive: Boolean(pathname?.startsWith(integrationLogsHref)),
        },
        ...(isSuperAdmin
          ? [
              {
                key: "admins",
                href: adminsHref,
                label: t(keys.admin.adminsTitle),
                icon: <AdminsNavIcon />,
                isActive: Boolean(pathname?.startsWith(adminsHref)),
              },
              {
                key: "appConfigs",
                href: appConfigsHref,
                label: t(keys.appConfigs.title),
                icon: <AppConfigsNavIcon />,
                isActive: Boolean(pathname?.startsWith(appConfigsHref)),
              },
              {
                key: "featureFlags",
                href: featureFlagsHref,
                label: t(keys.featureFlags.title),
                icon: <FeatureFlagsNavIcon />,
                isActive: Boolean(pathname?.startsWith(featureFlagsHref)),
              },
            ]
          : []),
      ],
    },
  ];

  const tabs = groups.flatMap((group) => group.items);
  const activeItem = tabs.find((item) => item.isActive);

  return { groups, tabs, activeItem };
}
